'use client';

import React, { useState, useEffect, useCallback } from 'react';

// CDN URLs for libraries (loaded at runtime, not bundled)
const RECHARTS_CDN = 'https://cdn.jsdelivr.net/npm/recharts@2.15.0/umd/Recharts.min.js';
const JSPDF_CDN = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js';
const AUTOTABLE_CDN = 'https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.4/dist/jspdf.plugin.autotable.min.js';

// Types for chart props
interface ChartDataItem {
  [key: string]: string | number;
}

interface LazyBarChartProps {
  data: ChartDataItem[];
  xAxisKey: string;
  bars: Array<{
    dataKey: string;
    fill: string;
    name?: string;
  }>;
  height?: number;
  className?: string;
  yAxisFormatter?: (value: number) => string;
  tooltipFormatter?: (value: number) => string;
  layout?: 'horizontal' | 'vertical';
  stacked?: boolean;
  cellColors?: string[] | ((entry: ChartDataItem, index: number) => string);
}

interface LazyPieChartProps {
  data: ChartDataItem[];
  dataKey: string;
  nameKey: string;
  colors: string[];
  height?: number;
  className?: string;
  innerRadius?: number;
  outerRadius?: number;
  showLabel?: boolean;
  labelFormatter?: (props: { name: string; percent: number }) => string;
  cellColors?: string[] | ((entry: ChartDataItem, index: number) => string);
}

interface LazyRadarChartProps {
  data: ChartDataItem[];
  dataKey: string;
  radars: Array<{
    dataKey: string;
    stroke: string;
    fill: string;
    fillOpacity?: number;
    name?: string;
  }>;
  height?: number;
  className?: string;
  angleAxisKey?: string;
  domain?: [number, number];
}

interface LazyAreaChartProps {
  data: ChartDataItem[];
  xAxisKey: string;
  areas: Array<{
    dataKey: string;
    stroke: string;
    fill: string;
    fillOpacity?: number;
    name?: string;
  }>;
  height?: number;
  className?: string;
  yAxisFormatter?: (value: number) => string;
  tooltipFormatter?: (value: number) => string;
  stacked?: boolean;
}

// Script loader utility
function loadScript(url: string, globalName: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if ((window as unknown as Record<string, unknown>)[globalName]) {
      resolve((window as unknown as Record<string, unknown>)[globalName]);
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;

    script.onload = () => {
      const lib = (window as unknown as Record<string, unknown>)[globalName];
      if (lib) {
        resolve(lib);
      } else {
        reject(new Error(`${globalName} not found after script load`));
      }
    };

    script.onerror = () => {
      reject(new Error(`Failed to load ${url}`));
    };

    document.head.appendChild(script);
  });
}

// Loading skeleton for charts
function ChartSkeleton({ height = 300 }: { height?: number }) {
  return (
    <div
      className="animate-pulse bg-gray-100 rounded-lg flex items-center justify-center"
      style={{ height }}
    >
      <div className="text-gray-400 text-sm">Loading chart...</div>
    </div>
  );
}

// Custom hook for loading Recharts from CDN
function useRecharts() {
  const [recharts, setRecharts] = useState<typeof import('recharts') | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    loadScript(RECHARTS_CDN, 'Recharts')
      .then((lib) => {
        if (mounted) {
          setRecharts(lib as typeof import('recharts'));
          setLoading(false);
        }
      })
      .catch((err) => {
        if (mounted) {
          console.error('Failed to load Recharts:', err);
          setError('Failed to load chart library');
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  return { recharts, error, loading };
}

// Lazy Bar Chart component
export function LazyBarChart({
  data,
  xAxisKey,
  bars,
  height = 300,
  className = '',
  yAxisFormatter,
  tooltipFormatter,
  layout = 'horizontal',
  stacked = false,
  cellColors,
}: LazyBarChartProps) {
  const { recharts, error, loading } = useRecharts();

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
        {error}
      </div>
    );
  }

  if (loading || !recharts) {
    return <ChartSkeleton height={height} />;
  }

  const { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } = recharts;

  const defaultYFormatter = (value: number) =>
    value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`;

  const defaultTooltipFormatter = (value: number) => `$${value.toLocaleString()}`;

  // Helper to get cell color
  const getCellColor = (entry: ChartDataItem, index: number, defaultFill: string): string => {
    if (!cellColors) return defaultFill;
    if (Array.isArray(cellColors)) {
      return cellColors[index % cellColors.length];
    }
    return cellColors(entry, index);
  };

  // For vertical layout, swap X and Y axis roles
  const isVertical = layout === 'vertical';

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout={layout}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          {isVertical ? (
            <>
              <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={yAxisFormatter || defaultYFormatter} />
              <YAxis type="category" dataKey={xAxisKey} tick={{ fontSize: 12 }} width={100} />
            </>
          ) : (
            <>
              <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={yAxisFormatter || defaultYFormatter} />
            </>
          )}
          <Tooltip
            formatter={(value) => [tooltipFormatter ? tooltipFormatter(value as number) : defaultTooltipFormatter(value as number), '']}
            contentStyle={{ fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {bars.map((bar, barIndex) => (
            <Bar
              key={barIndex}
              dataKey={bar.dataKey}
              fill={bar.fill}
              name={bar.name || bar.dataKey}
              stackId={stacked ? 'stack' : undefined}
            >
              {cellColors && data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getCellColor(entry, index, bar.fill)} />
              ))}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Lazy Pie Chart component
export function LazyPieChart({
  data,
  dataKey,
  nameKey,
  colors,
  height = 300,
  className = '',
  innerRadius = 0,
  outerRadius = 80,
  showLabel = true,
  labelFormatter,
  cellColors,
}: LazyPieChartProps) {
  const { recharts, error, loading } = useRecharts();

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
        {error}
      </div>
    );
  }

  if (loading || !recharts) {
    return <ChartSkeleton height={height} />;
  }

  const { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } = recharts;

  const defaultLabelFormatter = (props: { name: string; percent: number }) => {
    const { name, percent } = props;
    return percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : '';
  };

  // Helper to get cell color - cellColors takes precedence over colors
  const getCellColor = (entry: ChartDataItem, index: number): string => {
    if (cellColors) {
      if (Array.isArray(cellColors)) {
        return cellColors[index % cellColors.length];
      }
      return cellColors(entry, index);
    }
    return colors[index % colors.length];
  };

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            label={showLabel ? (labelFormatter || defaultLabelFormatter) as any : false}
            labelLine={showLabel}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getCellColor(entry, index)} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => [`$${(value as number).toLocaleString()}`, '']} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// Lazy Line Chart component
interface LazyLineChartProps {
  data: ChartDataItem[];
  xAxisKey: string;
  lines: Array<{
    dataKey: string;
    stroke: string;
    name?: string;
  }>;
  height?: number;
  className?: string;
}

export function LazyLineChart({
  data,
  xAxisKey,
  lines,
  height = 300,
  className = '',
}: LazyLineChartProps) {
  const { recharts, error, loading } = useRecharts();

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
        {error}
      </div>
    );
  }

  if (loading || !recharts) {
    return <ChartSkeleton height={height} />;
  }

  const { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } = recharts;

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {lines.map((line, index) => (
            <Line
              key={index}
              type="monotone"
              dataKey={line.dataKey}
              stroke={line.stroke}
              name={line.name || line.dataKey}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Lazy Radar Chart component
export function LazyRadarChart({
  data,
  dataKey,
  radars,
  height = 300,
  className = '',
  angleAxisKey,
  domain,
}: LazyRadarChartProps) {
  const { recharts, error, loading } = useRecharts();

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
        {error}
      </div>
    );
  }

  if (loading || !recharts) {
    return <ChartSkeleton height={height} />;
  }

  const { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, Legend, ResponsiveContainer } = recharts;

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} cx="50%" cy="50%" outerRadius="80%">
          <PolarGrid />
          <PolarAngleAxis dataKey={angleAxisKey || dataKey} tick={{ fontSize: 12 }} />
          <PolarRadiusAxis domain={domain} tick={{ fontSize: 10 }} />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {radars.map((radar, index) => (
            <Radar
              key={index}
              name={radar.name || radar.dataKey}
              dataKey={radar.dataKey}
              stroke={radar.stroke}
              fill={radar.fill}
              fillOpacity={radar.fillOpacity ?? 0.6}
            />
          ))}
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Lazy Area Chart component
export function LazyAreaChart({
  data,
  xAxisKey,
  areas,
  height = 300,
  className = '',
  yAxisFormatter,
  tooltipFormatter,
  stacked = false,
}: LazyAreaChartProps) {
  const { recharts, error, loading } = useRecharts();

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
        {error}
      </div>
    );
  }

  if (loading || !recharts) {
    return <ChartSkeleton height={height} />;
  }

  const { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } = recharts;

  const defaultYFormatter = (value: number) =>
    value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`;

  const defaultTooltipFormatter = (value: number) => `$${value.toLocaleString()}`;

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={yAxisFormatter || defaultYFormatter} />
          <Tooltip
            formatter={(value) => [tooltipFormatter ? tooltipFormatter(value as number) : defaultTooltipFormatter(value as number), '']}
            contentStyle={{ fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {areas.map((area, index) => (
            <Area
              key={index}
              type="monotone"
              dataKey={area.dataKey}
              stroke={area.stroke}
              fill={area.fill}
              fillOpacity={area.fillOpacity ?? 0.6}
              name={area.name || area.dataKey}
              stackId={stacked ? 'stack' : undefined}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// jsPDF types for the generator callback
interface JsPDFWithAutoTable {
  text: (text: string, x: number, y: number, options?: Record<string, unknown>) => void;
  setFontSize: (size: number) => void;
  setTextColor: (r: number, g: number, b: number) => void;
  setFont: (fontName: string, fontStyle?: string) => void;
  save: (filename: string) => void;
  internal: {
    pageSize: {
      getWidth: () => number;
      getHeight: () => number;
    };
  };
  lastAutoTable?: { finalY: number };
}

type AutoTableFunction = (doc: JsPDFWithAutoTable, options: Record<string, unknown>) => void;

// Export a hook for lazy PDF generation (loads from CDN)
export function useLazyPDF() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePDF = useCallback(async (
    generator: (doc: JsPDFWithAutoTable, autoTable: AutoTableFunction) => void
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      // Load jsPDF from CDN
      await loadScript(JSPDF_CDN, 'jspdf');
      await loadScript(AUTOTABLE_CDN, 'jspdf-plugin-autotable');

      const jsPDFLib = (window as unknown as Record<string, unknown>).jspdf as {
        jsPDF: new () => JsPDFWithAutoTable;
      };

      if (!jsPDFLib || !jsPDFLib.jsPDF) {
        throw new Error('jsPDF not loaded');
      }

      const doc = new jsPDFLib.jsPDF();

      // autoTable is added as a plugin to jsPDF
      const autoTable: AutoTableFunction = (d, options) => {
        const autoTableFn = (d as unknown as { autoTable: (options: Record<string, unknown>) => void }).autoTable;
        if (autoTableFn) {
          autoTableFn.call(d, options);
        }
      };

      generator(doc, autoTable);
    } catch (err) {
      console.error('Failed to load PDF libraries:', err);
      setError('Failed to generate PDF');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { generatePDF, isLoading, error };
}

// Export the chart skeleton for reuse
export { ChartSkeleton };
